import {
  Avatar,
  Box,
  Divider,
  Grid2,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { Fragment, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import SnackBar from "../../../component/SnackBar";
import {
  ResumeUploadPortalCall,
  SubmitResumeUpload,
} from "../../../redux/action/CoverSheet";
import ReferenceBackgroundImage from "../../../assets/images/ReferenceBackgroundImage.svg";
import { HandleSkeletion } from "../../../utils/constants";
import { CustomDiv, CustomMarkDown } from "../../chat-bot/style";
import { CommonGrid2 } from "../../common-components/StyledComponents";
import CommonTextField from "../../../component/Input";
import { CustomizeSpan } from "../../jobs/style";
import * as Yup from "yup";
import FileUpload from "../../matches/quick-match/components/FileUpload";
import CommonButton from "../../../component/Button";
import PopupTickIco from "../../../assets/icons/PopupTickIco";
import { Start } from "@mui/icons-material";
import SubmitTickIco from "../../../assets/icons/SubmitTickIco";
import { GetWhiteLabelAvatar } from "../../common-components/CoverSheetFunction";
import { platformWhiteLogo } from "../../../utils/logo";

export default function ResumeUpload() {
  const param = useParams();
  const resumeId = param.id;
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [resumeUploadData, setResumeUploadData] = useState({
    agency_name: "",
    remaining_resumes: {},
  });
  const jobDescriptionRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const handleCloseValidation = () => {
    setSnackbarState((prev) => ({
      ...prev,
      open: false,
    }));
  };
  const handleViewJobDescription = () => {
    if (jobDescriptionRef.current) {
      jobDescriptionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const fetchResumeUploadData = async () => {
    setIsLoading(true);

    try {
      let data = await ResumeUploadPortalCall(resumeId);
      setResumeUploadData((prev) => ({
        ...prev,
        ...data?.data,
      }));
      setIsLoading(false);
    } catch (error) {
      setSnackbarState({
        open: true,
        message: error.message,
        severity: "error",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchResumeUploadData();
  }, []);

  let Details = [
    {
      title: "Agency Name (Optional)",
      component: "input",
      fontSize: "15px",
      titleName: "agency_name",
      placeholderFontSize: "15px",
      placeholder: "Enter the Agency Name",
    },
    {
      title: "Email",
      component: "input",
      fontSize: "15px",
      titleName: "email",
      placeholderFontSize: "15px",
      placeholder: "Enter the Email",
    },
  ];

  const validationSchema = Yup.object({
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Invalid email address"
      )
      .test("allowed-email", "Email not allowed", function (value) {
        if (!value) return false;

        const remainingResumes = resumeUploadData?.remaining_resumes;
        const allowedEmails = Object.keys(remainingResumes);
        return allowedEmails.includes(value);
      }),
  });

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    commonFileValidationCheck(selectedFiles, allowedTypes, setFiles, files);
  };

  const handleDeleteFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    commonFileValidationCheck(droppedFiles, allowedTypes, setFiles, files);
  };

  const commonFileValidationCheck = (
    commonFiles,
    allowedTypes,
    setCommonFiles,
    existingFiles
  ) => {
    const validFiles = [];
    const invalidFiles = [];

    commonFiles.forEach((file) => {
      if (allowedTypes.includes(file.type)) {
        validFiles.push({
          file,
          name: file.name,
          size: `${(file.size / 1024).toFixed(1)} KB`,
          type: file.type,
          visible: false,
          delete: true,
          message: "",
        });
      } else {
        invalidFiles.push(file.name);
      }
    });

    const mergedFiles = [
      ...existingFiles,
      ...validFiles.filter(
        (newFile) =>
          !existingFiles.some(
            (existingFile) => existingFile.name === newFile.name
          )
      ),
    ];
    setCommonFiles(mergedFiles);

    if (invalidFiles.length > 0) {
      setSnackbarState({
        open: true,
        message: `Unsupported file types: ${invalidFiles.join(", ")}`,
        severity: "error",
      });
    }
  };
  const headerData = [
    {
      name: "Company",
      content: resumeUploadData?.company_name || " - ",
    },
    {
      name: "Location",
      content: resumeUploadData?.job_location || " - ",
    },
    {
      name: "Employment Type",
      content: resumeUploadData?.employment || " - ",
    },
  ];
  const lg = useMediaQuery("(min-width:767px)");

  const handleSubmit = async (values) => {
    const countCheck =
      values.remaining_resumes && values.remaining_resumes[values.email];

    if (files.length === 0) {
      setSnackbarState({
        open: true,
        message: "Upload the Files",
        severity: "error",
      });
    } else if (files.length > countCheck.remaining_resumes) {
      setSnackbarState({
        open: true,
        message: "File limit Exceeded",
        severity: "error",
      });
    } else {
      try {
        setButtonLoading(true);
        const formData = new FormData();
        files.forEach((fileObj, index) => {
          formData.append("resume_files", fileObj.file);
        });
        formData.append("email", values.email);
        formData.append("agency_name", values.agency_name);
        setFiles((prev) =>
          prev.map((item) => ({
            ...item,
            ...prev,
            delete: false,
          }))
        );
        let data = await SubmitResumeUpload(resumeId, formData);
        setSnackbarState({
          open: true,
          message: data.message,
          severity: "success",
        });
        setSubmitted(true);
        setButtonLoading(false);
      } catch (error) {
        setSnackbarState({
          open: true,
          message: error.message,
          severity: "error",
        });
        setFiles((prev) =>
          prev.map((item) => ({
            ...item,
            ...prev,
            delete: true,
          }))
        );
        setButtonLoading(false);
      }
    }
  };

  return (
    <>
      <Box padding="">
        <CustomDiv
          sx={{
            backgroundImage: `url(${ReferenceBackgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            ...(submitted && {
              height: "100vh",
              display: "flex",
              flexDirection: "column",
            }),
          }}
          boxShadow="1px 1px 5px 3px rgb(16 24 40 / 13%)"
          padding={lg ? "2rem" : "1rem"}
        >
          {isLoading ? (
            <HandleSkeletion
              height={50}
              width={lg ? "150px" : "60px"}
              transform={"scale(1)"}
            />
          ):(
            <Fragment>
              {resumeUploadData?.organization_info?.org_label || typeof resumeUploadData?.organization_info?.org_logo_url ===
              "string" ? (
                GetWhiteLabelAvatar(resumeUploadData?.organization_info?.org_logo_url, resumeUploadData?.organization_info?.org_label,lg,"#fff")
              ) : (
                platformWhiteLogo({width: lg ? "160px" : "130px", height: "55px"})
              )}
            </Fragment>
          )}
          
          <CommonGrid2
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            color={"#fff"}
            textAlign={"center"}
            margin={"2rem 0px 2rem"}
            alignItems={"center"}
            gap={"1rem"}
            height={"100%"}
          >
            <Typography
              variant={lg ? "h5" : "h5xs"}
              color="#D5DADD"
              textAlign="center"
              fontWeight={500}
              backgroundColor="#402D66"
              width="fit-content"
              borderRadius="10px"
              padding="7px 12px"
              margin="1rem 0px"
            >
              {!submitted ? (
                <>
                  <CustomizeSpan color="#FA4F96" fontWeight={700}>
                    {resumeUploadData?.personal_details?.candidate_first_name}
                  </CustomizeSpan>{" "}
                  Hi! Kindly upload the relevant resumes for
                </>
              ) : (
                <>
                  Thank You{" "}
                  <CustomizeSpan color="#FA4F96" fontWeight={700}>
                    {resumeUploadData?.personal_details?.candidate_first_name?.toUpperCase()}
                  </CustomizeSpan>
                      . Your {files.length > 1 ? "resumes have" : "resume has"} been uploaded successfully.
                </>
              )}
            </Typography>
            <Typography
              variant={lg ? "h2" : "h2xs"}
              color="#D5DADD"
              textAlign={"center"}
              marginTop={"4px"}
              fontWeight={500}
            >
              Job Title
            </Typography>
            {isLoading ? (
              <HandleSkeletion
                height={50}
                width={lg ? "400px" : "100%"}
                background="rgb(255 255 255 / 33%)"
                margin={"10px auto 0px"}
                transform={"scale(1)"}
              />
            ) : (
              <Typography
                variant={lg ? "h1" : "h2"}
                fontSize={lg && "50px"}
                color="#FFFFFF"
                marginTop={"-5px"}
                fontWeight={700}
                textTransform={"capitalize"}
              >
                {resumeUploadData?.job_name}
              </Typography>
            )}

            <CommonGrid2
              display={"flex"}
              gap={lg ? "3rem" : "1rem"}
              alignItems={"center"}
              marginTop={lg ? "1rem" : "10px"}
            >
              {isLoading.loading ? (
                <Fragment>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <HandleSkeletion
                      height={50}
                      width={lg ? "150px" : "60px"}
                      background="rgb(255 255 255 / 33%)"
                      transform={"scale(1)"}
                    />
                  ))}
                </Fragment>
              ) : (
                <Fragment>
                  {headerData.map((item, index) => (
                    <Fragment>
                      <CommonGrid2
                        display={"flex"}
                        flexDirection={"column"}
                        gap={"5px"}
                        alignItems={"start"}
                      >
                        <Typography
                          variant={lg ? "body4" : "h5xs"}
                          fontWeight={400}
                          color="#D5DADD"
                          textTransform={"capitalize"}
                        >
                          {/* {resumeUploadData.company_name} */}
                          {item.name}
                        </Typography>

                        <Typography
                          variant={lg ? "h2xs" : "body1xs"}
                          fontWeight={800}
                          textTransform={"capitalize"}
                          maxWidth={"200px"}
                          sx={{
                            wordBreak: "break-word",
                          }}
                        >
                          {item.content}
                        </Typography>
                      </CommonGrid2>
                      {index !== 2 && (
                        <CommonGrid2
                          padding={"2px"}
                          background="#2E2440"
                          borderRadius={"10px"}
                          height={"55px"}
                          // boxShadow={"0px 0px 1px 0px"}
                        />
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              )}
            </CommonGrid2>
            {isLoading.loading ? (
              <HandleSkeletion
                height={40}
                width={lg ? "400px" : "100%"}
                background="rgb(255 255 255 / 33%)"
                margin={"10px auto 0px"}
                transform={"scale(1)"}
              />
            ) : (
              <Fragment>
                {!submitted ? (
                  <CommonButton
                    customStyle={{
                      minWidth: "150px",
                      maxWidth: "200px",
                      width: "100%",
                    }}
                    color="black"
                    background="#FFFFFF"
                    mobileFontSize="13px"
                    fontWeight={700}
                    marginTop={"1rem"}
                    value={"View Job Description"}
                    padding={"10px 1.5rem"}
                    onClick={() => {
                      setTimeout(() => {
                        handleViewJobDescription();
                      }, 100);
                    }}
                    disabled={isLoading}
                  />
                ) : (
                  <Box
                    display={"inline-flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    backgroundColor={"#079455"}
                    color={"#FFFFFF"}
                    borderRadius={"8px"}
                    fontWeight={600}
                    marginTop={"1rem"}
                    gap={1}
                    padding={"10px 20px"}
                  >
                    <SubmitTickIco />
                    Submitted Successfully
                  </Box>
                )}
              </Fragment>
            )}
          </CommonGrid2>
        </CustomDiv>
        {!submitted && (
          <CustomDiv
            position="relative"
            borderRadius="10px"
            boxShadow="1px 1px 5px 3px rgb(16 24 40 / 13%)"
            padding={lg ? "4rem" : "1rem"}
          >
            <Formik
              enableReinitialize={true}
              initialValues={resumeUploadData}
              validationSchema={validationSchema}
              onSubmit={async (values, actions) => {
                handleSubmit(values);
              }}
            >
              {({ values, setFieldValue }) => {
                const countCheck =
                  values &&
                  values.remaining_resumes &&
                  values.remaining_resumes[values.email];

                return (
                  <Form
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.preventDefault();
                    }}
                  >
                    <Grid2 container spacing={4}>
                      <Grid2 container spacing={3} size={12}>
                        <Fragment>
                          <Grid2 size={12} ref={jobDescriptionRef}>
                            <Typography
                              variant={lg ? "h2" : "h3"}
                              fontWeight={600}
                              color={"#101828"}
                              display={"flex"}
                              alignItems={"center"}
                              width={"100%"}
                              marginTop={lg ? 5 : 2.5}
                            >
                              Job Description
                            </Typography>
                            {isLoading ? (
                              <HandleSkeletion height={120} />
                            ) : (
                              <CustomMarkDown fontSize={lg ? "14px" : "13px"}>
                                {typeof resumeUploadData?.job_info === "string"
                                  ? resumeUploadData?.job_info.trim()
                                  : JSON.stringify(resumeUploadData?.job_info)}
                              </CustomMarkDown>
                            )}
                          </Grid2>
                          <Grid2 size={12}>
                            <Typography
                              variant={lg ? "h4" : "h6"}
                              fontWeight={600}
                              color={"#101828"}
                              display={"flex"}
                              alignItems={"center"}
                              width={"100%"}
                            >
                              Agency Details
                            </Typography>
                            <CommonGrid2 container spacing={lg ? 3 : 2}>
                              {Details.map((item, index) => (
                                <CommonGrid2
                                  item
                                  size={{
                                    xs: 12,
                                    md: 6,
                                    sm: 6,
                                  }}
                                  gap={"5px"}
                                  marginTop={"1rem"}
                                >
                                  <CommonGrid2
                                    display={"flex"}
                                    justifyContent={"space-between"}
                                    alignItems={"center"}
                                    width={"100%"}
                                  >
                                    <Typography
                                      variant={lg ? "h6" : "body1"}
                                      color="#344054"
                                      marginBottom={"10px"}
                                    >
                                      {item.title}{" "}
                                      {index !== 0 && (
                                        <CustomizeSpan color={"red"}>
                                          {" "}
                                          *
                                        </CustomizeSpan>
                                      )}
                                    </Typography>
                                  </CommonGrid2>
                                  {isLoading ? (
                                    <HandleSkeletion
                                      height={50}
                                      transform={"scale(1)"}
                                    />
                                  ) : (
                                    <Fragment>
                                      {item.component === "input" && (
                                        <Field name={item.titleName}>
                                          {({ field, form }) => {
                                            return (
                                              <CommonTextField
                                                placeholder={item.placeholder}
                                                padding="10px"
                                                error={Boolean(
                                                  form.errors[item.titleName]
                                                )}
                                                placeholderFontSize={"15px"}
                                                helperText={
                                                  form.errors[item.titleName]
                                                }
                                                value={values[item.titleName]}
                                                onChange={(e) =>
                                                  setFieldValue(
                                                    item.titleName,
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            );
                                          }}
                                        </Field>
                                      )}
                                    </Fragment>
                                  )}
                                </CommonGrid2>
                              ))}
                            </CommonGrid2>
                          </Grid2>
                          <Grid2 size={12}>
                            <Typography
                              variant={lg ? "h4" : "h6"}
                              fontWeight={600}
                              color={"#101828"}
                              display={"flex"}
                              alignItems={"center"}
                              width={"100%"}
                            >
                              Upload resumes
                              {countCheck?.max_resumes -
                                countCheck?.resumes_uploaded ===
                              0 ? (
                                <CustomizeSpan
                                  color="red"
                                  margin="0px 0px 0px 6px"
                                >
                                  {`${" "}Limit  Exceeded for this Email`}
                                </CustomizeSpan>
                              ) : (
                                <Fragment>
                                  {countCheck?.max_resumes && (
                                    <CustomizeSpan
                                      color="#6941C6"
                                      margin="0px 0px 0px 6px"
                                    >
                                      {`(Limited to ${
                                        countCheck?.max_resumes -
                                        countCheck?.resumes_uploaded
                                      })`}
                                    </CustomizeSpan>
                                  )}
                                </Fragment>
                              )}
                            </Typography>
                            <CommonGrid2
                              size={{
                                xs: 12,
                                md: 8,
                                sm: 12,
                              }}
                              marginTop={"1rem"}
                            >
                              {isLoading ? (
                                <HandleSkeletion height={120} />
                              ) : (
                                <FileUpload
                                  onDragOver={handleDragOver}
                                  onDragLeave={handleDragLeave}
                                  onDrop={handleDrop}
                                  onChange={handleFileUpload}
                                  isDragging={isDragging}
                                  files={files}
                                  handleDeleteFile={handleDeleteFile}
                                  disabled={isLoading || buttonLoading}
                                />
                              )}
                              <CommonGrid2 textAlign={"end"}>
                                <CommonButton
                                  type="submit"
                                  value={"Submit Resume"}
                                  padding={"10px 2rem"}
                                  disabled={
                                    buttonLoading ||
                                    isLoading ||
                                    countCheck?.remaining_resumes === 0
                                  }
                                  width={!lg && "100%"}
                                  loading={buttonLoading}
                                  fontWeight={700}
                                  mobileFontSize="13px"
                                />
                              </CommonGrid2>
                            </CommonGrid2>
                          </Grid2>
                        </Fragment>
                      </Grid2>
                    </Grid2>
                  </Form>
                );
              }}
            </Formik>

            <SnackBar
              snackbarState={snackbarState}
              handleCloseValidation={handleCloseValidation}
            />
          </CustomDiv>
        )}
      </Box>
    </>
  );
}
